import React from 'react';
import { Box, Typography, Button, alpha, Paper } from '@mui/material';
import { MdSecurity } from 'react-icons/md';
import TextBox from '../../../components/input/TextBox';
import { getOrgTokens } from '../../../theme/style/organizationPalette';
import { TableHeaderContainer } from '../../../components/container/TableHeaderContainer';
import { TableTitleContainer } from '../../../components/container/TableTitleContainer';

const SecurityTab = ({
    t,
    mode,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    isVerifyingNewPassword,
    setIsVerifyingNewPassword,
    handleVerifyCurrentPassword,
    handleChangePassword,
    message,
    isSupportAccessEnabled,
    handleToggleSupportAccess
}) => {
    const isDark = mode === 'dark';
    const tok = getOrgTokens(isDark);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: tok.spacing.pageGap }}>
            <Paper variant="Table Container">
                <Box sx={{ 
                    ...tok.sectionHeading, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    pl: 0,
                    position: 'relative',
                    p: tok.spacing.pageGap,
                    mb: 0
                }}>
                    <Box sx={{ 
                        width: tok.sectionHeading.barWidth, 
                        height: '24px', 
                        bgcolor: tok.sectionHeading.barColor, 
                        borderRadius: '2px' 
                    }} />
                    <Typography sx={{ 
                        fontFamily: tok.sectionHeading.fontFamily,
                        fontSize: tok.sectionHeading.fontSize,
                        fontWeight: tok.sectionHeading.fontWeight,
                        color: tok.sectionHeading.color
                    }}>
                        {t("Security & Password")}
                    </Typography>
                    {isVerifyingNewPassword && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setIsVerifyingNewPassword(false)}
                            sx={{
                                ml: 'auto',
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 2,
                                py: 0.5,
                                borderColor: alpha(tok.sectionHeading.barColor, 0.2),
                                color: tok.sectionHeading.barColor
                            }}
                        >
                            {t("Back")}
                        </Button>
                    )}
                </Box>

                <Box sx={{ p: tok.spacing.pageGap, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: isDark ? 'rgba(245, 158, 11, 0.05)' : 'rgba(245, 158, 11, 0.03)',
                        border: '1px solid',
                        borderColor: 'rgba(245, 158, 11, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#fbbf24' : '#d97706' }}>
                                {t("Grant Support Access")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                {t("Allow Master Admins to log in for troubleshooting.")}
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            variant={isSupportAccessEnabled ? "contained" : "outlined"}
                            color={isSupportAccessEnabled ? "warning" : "inherit"}
                            onClick={handleToggleSupportAccess}
                            sx={{ fontWeight: 700, borderRadius: 2.5, boxShadow: 'none', border: !isSupportAccessEnabled ? '1px solid rgba(0,0,0,0.1)' : 'none' }}
                        >
                            {isSupportAccessEnabled ? t("Enabled") : t("Enable")}
                        </Button>
                    </Box>

                    <Box sx={{ width: '100%', display: "flex", flexDirection: "column", gap: 3 }}>
                        {!isVerifyingNewPassword ? (
                            <>
                                <TextBox
                                    label={t("Current Password")}
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    placeholder={t("Enter current password to verify")}
                                />
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleVerifyCurrentPassword}
                                    sx={{
                                        py: 1.2,
                                        borderRadius: 2.5,
                                        fontWeight: 600,
                                        textTransform: "none",
                                        bgcolor: tok.sectionHeading.barColor,
                                        boxShadow: 'none',
                                        "&:hover": {
                                            bgcolor: alpha(tok.sectionHeading.barColor, 0.8),
                                            boxShadow: 'none',
                                        }
                                    }}
                                >
                                    {t("Verify Current Password")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Box sx={{
                                    p: 1.5,
                                    borderRadius: 3,
                                    bgcolor: 'rgba(16, 185, 129, 0.05)',
                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                    mb: 1
                                }}>
                                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700, display: 'block' }}>
                                        ✓ {t("Verified Successfully")}
                                    </Typography>
                                </Box>
                                <TextBox
                                    label={t("New Password")}
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder={t("Create a strong password")}
                                />
                                <TextBox
                                    label={t("Confirm New Password")}
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder={t("Repeat your new password")}
                                />
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleChangePassword}
                                    sx={{
                                        py: 1.2,
                                        borderRadius: 2.5,
                                        fontWeight: 600,
                                        textTransform: "none",
                                        bgcolor: '#10b981',
                                        boxShadow: 'none',
                                        "&:hover": {
                                            bgcolor: '#059669',
                                            boxShadow: 'none',
                                        }
                                    }}
                                >
                                    {t("Update Password Now")}
                                </Button>
                            </>
                        )}

                        {message.text && (
                            <Typography
                                sx={{
                                    color: message.type === "error" ? "#ef4444" : "#10b981",
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    bgcolor: message.type === "error" ? "rgba(239, 68, 68, 0.05)" : "rgba(16, 185, 129, 0.05)",
                                    p: 1.5,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: message.type === "error" ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)"
                                }}
                            >
                                {message.text}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default SecurityTab;
